// /pam/policies/:product — the list. On top, how a sign-in is checked: the
// four stages in engine order (Deny, Require MFA, Allow, Fallback) with their
// counts; clicking one shows only that stage. Then search and On/Paused, and
// one table grouped by stage, the same columns on every product; only the
// Where text comes from the product. Never scrolls sideways: below 80rem the
// conditions column folds under the name.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, Switch, Table, Tooltip, message } from "antd";
import { Search } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { AN_COLORS, AN_FONT, AN_MONO } from "../../design-system/tokens";
import { PageHeader, PageShell, RowActionMenu, TableCard, anTableProps, fmtDate } from "../../design-system/tableUi";
import { conditionRows, listBadges } from "../../components/policyShell/conditionsModel";
import { groupKeyOf, groupPolicies, whoCellText } from "../../components/policyShell/policyModel";
import { POLICY_GROUPS } from "../../components/policyShell/signals";
import { ActionPill, Segmented, Tag, actionMeta } from "./ui";
import { ProductGate, useAdapterData, useMediaQuery, usePolicyActions } from "./usePolicyPage";
import { policiesPath } from "./policyProducts";

const noop = () => {};
const WIDE = "(min-width: 80rem)";
// A group heading row: a quiet band, the same height on every group.
const GROUP_CELL = { background: AN_COLORS.bg, padding: "0.5rem 1rem", cursor: "default" };
const STAGE_FLOW = { block: "block", mfa_required: "mfa_required", allow: "allow", fallback: "allow" };

// When a policy last changed, from whichever field the product's API sends.
export const changedAt = (row) => row?.updatedAt || row?.updated_at || row?.modifiedAt || row?.createdAt || row?.created_at || row?.CreatedAt || null;

// One chip per condition (its summary in the tooltip), and on SSH Approval
// and Recorded. Fallback is the list's own group.
export function ListBadges({ policyJson, adapter, empty = null }) {
  const f = adapter?.features || {};
  const subject = adapter?.labels?.subject;
  const conds = conditionRows(withoutExclusions(policyJson?.conditions), { subject, product: adapter?.key });
  const extras = listBadges(policyJson, { approval: !!f.approval, session: !!f.session, subject, product: adapter?.key }).filter(
    (b) => b.key !== "fallback" && b.key !== "conditions",
  );
  if (!conds.length && !extras.length) return empty;
  return (
    <span style={{ display: "inline-flex", flexWrap: "wrap", gap: "0.25rem" }}>
      {conds.map((c) => (
        <Tooltip key={c.key} title={c.hidden ? c.summary : `${c.label}: ${c.summary}`}>
          <span>
            <Tag>{c.label}</Tag>
          </span>
        </Tooltip>
      ))}
      {extras.map((b) => (
        <Tooltip key={b.key} title={b.tooltip}>
          <span>
            <Tag>{b.label}</Tag>
          </span>
        </Tooltip>
      ))}
    </span>
  );
}

function withoutExclusions(conditions) {
  if (!conditions || typeof conditions !== "object") return {};
  const next = { ...conditions };
  if (next.identity && typeof next.identity === "object") {
    const { excludeUsers, excludeGroups, ...rest } = next.identity;
    if (Object.keys(rest).length) next.identity = rest;
    else delete next.identity;
  }
  return next;
}

// How the engine picks between matching policies. Database: the strictest
// wins (MostRestrictive). The others: Deny wins, then the first match in order.
export const precedenceText = (adapter) =>
  `${precedenceLead(adapter)}${adapter?.features?.fallback ? " Fallback only when nothing else matches." : ""}`;
const precedenceLead = (adapter) =>
  adapter?.features?.mfaWinsOverAllow
    ? "The strictest matching policy wins: Deny, then Require MFA, then Allow."
    : "A matching Deny always wins; otherwise the first matching policy decides.";

/** "How a sign-in is checked": the four stages, each a filter. */
function StageStrip({ counts, stage, onStage, subject, strictest, note, fallback }) {
  return (
    <section aria-label={`How a ${subject} is checked`} style={{ marginBottom: "1rem" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: "0.75rem", marginBottom: "0.5rem" }}>
        <span style={{ fontSize: "0.8125rem", fontWeight: 600, color: AN_COLORS.ink2 }}>How a {subject} is checked</span>
        <span style={{ fontSize: "0.75rem", color: AN_COLORS.muted }}>{note}</span>
        {stage && (
          <button
            type="button"
            onClick={() => onStage(null)}
            style={{ marginLeft: "auto", border: "none", background: "transparent", color: AN_COLORS.primary, cursor: "pointer", fontSize: "0.8125rem", fontWeight: 500, fontFamily: AN_FONT }}
          >
            Show all
          </button>
        )}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(12rem, 1fr))", gap: "0.5rem" }}>
        {POLICY_GROUPS.filter((g) => fallback || g.key !== "fallback" || counts.fallback).map((g, i) => {
          const m = actionMeta(STAGE_FLOW[g.key]);
          const on = stage === g.key;
          const n = counts[g.key] || 0;
          return (
            <button
              key={g.key}
              type="button"
              aria-pressed={on}
              aria-label={`${g.label}: ${n} ${n === 1 ? "policy" : "policies"}`}
              onClick={() => onStage(on ? null : g.key)}
              style={{
                display: "grid",
                gridTemplateColumns: "auto 1fr auto",
                alignItems: "center",
                gap: "0.625rem",
                textAlign: "left",
                padding: "0.75rem 0.875rem",
                borderRadius: "0.625rem",
                border: `1px solid ${on ? m.solid : AN_COLORS.line}`,
                boxShadow: on ? `inset 0 0 0 1px ${m.solid}` : "none",
                background: on ? m.bg : AN_COLORS.surface,
                cursor: "pointer",
                fontFamily: AN_FONT,
                opacity: n || on ? 1 : 0.6,
              }}
            >
              <span
                aria-hidden
                style={{
                  width: "1.5rem",
                  height: "1.5rem",
                  borderRadius: "50%",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  background: g.key === "fallback" ? AN_COLORS.tint : m.bg,
                  color: g.key === "fallback" ? AN_COLORS.ink3 : m.fg,
                  border: `1px solid ${g.key === "fallback" ? AN_COLORS.line : m.bd}`,
                }}
              >
                {i + 1}
              </span>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", fontSize: "0.875rem", fontWeight: 600, color: AN_COLORS.ink }}>{g.label}</span>
                <span style={{ display: "block", fontSize: "0.75rem", color: AN_COLORS.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {g.key === "block" ? "Always wins" : g.key === "fallback" ? "When nothing else matches" : g.key === "mfa_required" ? (strictest ? "Wins over Allow" : "Then by order") : strictest ? "When nothing stricter matches" : "Then by order"}
                </span>
              </span>
              <span aria-hidden style={{ fontFamily: AN_MONO, fontSize: "1.125rem", fontWeight: 600, color: n ? AN_COLORS.ink : AN_COLORS.muted, fontVariantNumeric: "tabular-nums" }}>
                {n}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

/** One list item per row: what grouping, filtering and the cells need. */
export function listItem(adapter, row) {
  const pj = row.policyJson || {};
  const who = adapter.listWho(row);
  const conds = conditionRows(pj.conditions, { product: adapter.key });
  return {
    row,
    id: adapter.rowId(row),
    name: row.policyName || pj.policyName || "",
    flow: adapter.actionOf(pj),
    fallback: !!(adapter.features?.fallback && pj.fallback),
    everyone: !!who.everyone,
    whoText: whoCellText(who),
    where: adapter.listWhere(row) || "",
    conditionCount: conds.length,
    active: adapter.isActive(row),
    changed: changedAt(row),
  };
}

export default function PoliciesListPage() {
  return <ProductGate render={(p) => <PoliciesList key={p.product} {...p} />} />;
}

const EMPTY_FILTERS = { search: "", status: null, stage: null };

function PoliciesList({ product, adapter, flags }) {
  const navigate = useNavigate();
  const [messageApi, messageContext] = message.useMessage();
  const [modalApi, modalContext] = Modal.useModal();
  const data = useAdapterData(adapter, null, noop);
  const wide = useMediaQuery(WIDE);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const labels = adapter.labels || {};

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    Promise.resolve(adapter.list())
      .then((list) => setRows(Array.isArray(list) ? list : []))
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error("Policy list failed", e?.raw ?? e);
        setLoadError(true);
      })
      .finally(() => setLoading(false));
  }, [adapter]);
  useEffect(() => {
    load();
  }, [load]);

  const { setActive, remove } = usePolicyActions(adapter, { messageApi, modalApi, after: load });

  const items = useMemo(() => rows.map((r) => listItem(adapter, r)), [rows, adapter]);
  // Search and status first: the stage counts follow them.
  const searched = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return items.filter((it) => {
      if (q && ![it.name, it.whoText, it.where].some((t) => String(t).toLowerCase().includes(q))) return false;
      if (filters.status === "on" && !it.active) return false;
      if (filters.status === "off" && it.active) return false;
      return true;
    });
  }, [items, filters.search, filters.status]);
  const counts = useMemo(() => searched.reduce((acc, it) => ({ ...acc, [groupKeyOf(it)]: (acc[groupKeyOf(it)] || 0) + 1 }), {}), [searched]);
  const filtered = useMemo(() => (filters.stage ? searched.filter((it) => groupKeyOf(it) === filters.stage) : searched), [searched, filters.stage]);
  const hasDates = items.some((it) => it.changed);
  const groups = useMemo(
    () => groupPolicies(filtered, { mfaWinsOverAllow: !!adapter.features?.mfaWinsOverAllow, specificity: adapter.features?.specificity }),
    [filtered, adapter],
  );

  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const anyFilter = !!(filters.search.trim() || filters.status || filters.stage);

  const detail = (it) => policiesPath(product, encodeURIComponent(it.id));
  const goNew = () => navigate(policiesPath(product, "new"));
  const Extra = adapter.features?.ListHeaderExtra;

  // Group headings are rows of the same table, so every group shares one
  // header row and one set of column edges.
  const groupHeading = (g) => (
    <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem", flexWrap: "wrap" }}>
      <span aria-hidden style={{ width: "0.5rem", height: "0.5rem", borderRadius: "50%", alignSelf: "center", background: g.key === "fallback" ? AN_COLORS.muted : actionMeta(g.key).solid }} />
      <span style={{ fontWeight: 600, fontSize: "0.8125rem", color: AN_COLORS.ink }}>{g.label}</span>
      <span style={{ fontFamily: AN_MONO, fontSize: "0.75rem", color: AN_COLORS.muted, fontVariantNumeric: "tabular-nums" }}>{g.items.length}</span>
      {g.line && <span style={{ fontSize: "0.8125rem", color: AN_COLORS.muted }}>· {g.line}</span>}
    </div>
  );
  const spanCell = (it) => (it.group ? { colSpan: 0 } : {});

  const baseColumns = [
    {
      key: "on",
      title: "On",
      width: "4.5rem",
      render: (_, it) => (
        <span onClick={(e) => e.stopPropagation()} role="presentation">
          <Switch size="small" checked={it.active} onChange={(v) => setActive(it.row, v)} aria-label={`${it.name} on or off`} />
        </span>
      ),
    },
    {
      key: "name",
      title: "Policy",
      ellipsis: false,
      render: (_, it) => (
        <div style={{ minWidth: 0, display: "grid", gap: "0.25rem", opacity: it.active ? 1 : 0.6 }}>
          <span style={{ display: "flex", alignItems: "center", gap: "0.5rem", minWidth: 0 }}>
            <Link
              to={detail(it)}
              state={{ policy: it.row }}
              onClick={(e) => e.stopPropagation()}
              style={{ color: AN_COLORS.ink, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {it.name || "Untitled policy"}
            </Link>
            {!it.active && <Tag tone="warn">Paused</Tag>}
          </span>
          <span
            title={[it.whoText, it.where].filter(Boolean).join(" → ")}
            style={{ color: AN_COLORS.muted, fontSize: "0.8125rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {it.whoText}
            {it.where && (
              <>
                <span aria-hidden style={{ margin: "0 0.375rem", color: AN_COLORS.ink3 }}>→</span>
                {it.where}
              </>
            )}
          </span>
          {!wide && <ListBadges policyJson={it.row.policyJson} adapter={adapter} />}
        </div>
      ),
    },
    {
      key: "action",
      title: "Then",
      width: "10rem",
      render: (_, it) => <ActionPill flow={it.flow} />,
    },
    ...(wide ? [{ key: "when", title: "Only when", width: "22%", render: (_, it) => <ListBadges policyJson={it.row.policyJson} adapter={adapter} empty={<span style={{ color: AN_COLORS.muted, fontSize: "0.8125rem" }}>Every time</span>} /> }] : []),
    ...(hasDates && wide
      ? [
          {
            key: "changed",
            title: "Changed",
            width: "9rem",
            render: (_, it) => <span style={{ color: AN_COLORS.muted, fontSize: "0.8125rem" }}>{it.changed ? fmtDate(it.changed).split(",").slice(0, 2).join(",") : "—"}</span>,
          },
        ]
      : []),
    ...(flags.appliedColumn
      ? [
          {
            key: "applied",
            title: "Applied",
            width: "6rem",
            align: "right",
            render: (_, it) => (
              <span style={{ fontFamily: AN_MONO, fontVariantNumeric: "tabular-nums" }}>
                {it.row.appliedLast7d != null ? `${it.row.appliedLast7d} / 7d` : "—"}
              </span>
            ),
          },
        ]
      : []),
    {
      key: "menu",
      title: "",
      width: "3rem",
      // Menu clicks happen in a portal, but React still bubbles them to the
      // row: stop them here so Edit doesn't also open the detail page.
      render: (_, it) => (
        <span onClick={(e) => e.stopPropagation()} role="presentation">
          <RowActionMenu
            ariaLabel={`Actions for ${it.name}`}
            items={[
              { key: "view", label: "View", onClick: () => navigate(detail(it), { state: { policy: it.row } }) },
              { key: "edit", label: "Edit", onClick: () => navigate(`${detail(it)}/edit`, { state: { policy: it.row } }) },
              { key: "duplicate", label: "Duplicate", onClick: () => navigate(policiesPath(product, "new"), { state: { duplicateOf: it.row } }) },
              { type: "divider" },
              { key: "delete", label: "Delete", danger: true, onClick: () => remove(it.row) },
            ]}
          />
        </span>
      ),
    },
  ];

  const columns = baseColumns.map((c, i) =>
    i === 0
      ? {
          ...c,
          render: (v, it, idx) => (it.group ? groupHeading(it.group) : c.render(v, it, idx)),
          onCell: (it) => (it.group ? { colSpan: baseColumns.length, style: GROUP_CELL } : {}),
        }
      : { ...c, onCell: spanCell, render: (v, it, idx) => (it.group ? null : c.render(v, it, idx)) },
  );
  const tableRows = groups.flatMap((g) => [{ id: `group:${g.key}`, group: g }, ...g.items]);

  const emptyCard = (children) => (
    <TableCard>
      <div style={{ padding: "3rem 1.5rem", textAlign: "center", display: "grid", gap: "0.75rem", justifyItems: "center" }}>{children}</div>
    </TableCard>
  );

  let body;
  if (loading && !rows.length) {
    body = emptyCard(<span style={{ color: AN_COLORS.muted }}>Loading policies…</span>);
  } else if (loadError && !rows.length) {
    body = emptyCard(
      <>
        <span style={{ color: AN_COLORS.ink2 }}>Couldn't load policies.</span>
        <Button onClick={load}>Try again</Button>
      </>,
    );
  } else if (!rows.length) {
    body = emptyCard(
      <>
        <div style={{ fontSize: "1rem", fontWeight: 600, color: AN_COLORS.ink }}>No policies yet.</div>
        <div style={{ color: AN_COLORS.muted, fontSize: "0.875rem" }}>{labels.emptySentence}</div>
        <Button type="primary" onClick={goNew}>
          New policy
        </Button>
      </>,
    );
  } else if (!filtered.length) {
    body = emptyCard(
      <>
        <div style={{ color: AN_COLORS.ink2 }}>No policies match these filters.</div>
        <Button onClick={() => setFilters(EMPTY_FILTERS)}>Clear filters</Button>
      </>,
    );
  } else {
    body = (
      <TableCard>
        <Table
          {...anTableProps}
          rowKey="id"
          columns={columns}
          dataSource={tableRows}
          pagination={false}
          tableLayout="fixed"
          rowClassName={(it) => (it.group ? "an-policy-group-row" : "")}
          onRow={(it) =>
            it.group
              ? { "aria-label": `${it.group.label} policies` }
              : { onClick: () => navigate(detail(it), { state: { policy: it.row } }), style: { cursor: "pointer" } }
          }
        />
      </TableCard>
    );
  }

  return (
    <PageShell>
      {messageContext}
      {modalContext}
      <PageHeader
        eyebrow={labels.eyebrow || "Policies"}
        title="Policies"
        subtitle={labels.listDescription}
        actions={
          <>
            {Extra && <Extra reload={load} data={data} />}
            <Button type="primary" onClick={goNew}>
              New policy
            </Button>
          </>
        }
      />
      {rows.length > 0 && (
        <>
          <StageStrip
            counts={counts}
            stage={filters.stage}
            onStage={(v) => set({ stage: v })}
            subject={labels.subject || "sign-in"}
            strictest={!!adapter.features?.mfaWinsOverAllow}
            note={precedenceText(adapter)}
            fallback={!!adapter.features?.fallback}
          />
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
            <Input
              allowClear
              prefix={<Search size={14} aria-hidden style={{ color: AN_COLORS.muted }} />}
              placeholder="Search by name, who or where"
              value={filters.search}
              onChange={(e) => set({ search: e.target.value })}
              aria-label="Search policies"
              style={{ width: "20rem", maxWidth: "100%" }}
            />
            <Segmented
              ariaLabel="Status"
              value={filters.status || "all"}
              onChange={(v) => set({ status: v === "all" ? null : v })}
              options={[
                { value: "all", label: "All" },
                { value: "on", label: "On" },
                { value: "off", label: "Paused" },
              ]}
            />
            <span style={{ marginLeft: "auto", fontSize: "0.8125rem", color: AN_COLORS.muted }}>
              Showing {filtered.length} of {rows.length}
            </span>
            {anyFilter && (
              <Button size="small" onClick={() => setFilters(EMPTY_FILTERS)}>
                Clear filters
              </Button>
            )}
          </div>
        </>
      )}
      {body}
    </PageShell>
  );
}
